import * as THREE from "three";

// Points of Interest — fixed world locations the cat AI can decide to
// visit when a relevant need crosses its threshold. Each POI ships a
// renderable mesh (so the user can see *why* the cat is heading over
// there) plus the data the supervisor in cat-ai.ts needs: the need it
// satisfies, the animation clip to play once arrived, and how long to
// stay before counting the need as sated.
//
// New POI types should be added to NeedKind in cat-ai.ts and the cat's
// clip pool in cat.ts before being referenced here.

export type PoiKind = "food" | "water";

export interface PointOfInterest {
  id: string;
  kind: PoiKind;
  /** World-space position of the interaction point — usually the floor
   * directly under the bowl. The cat steers to here, not the bowl's
   * center, so it stops next to the bowl rather than on top of it. */
  position: THREE.Vector3;
  /** Seconds the cat dwells at the POI playing `clip` before the need
   * is considered satisfied. */
  duration: number;
  /** Animation clip from cat.glb the cat plays during interaction. */
  clip: string;
  /** Visible mesh for this POI. Already added to the scene. */
  mesh: THREE.Object3D;
}

export interface PoiHandle {
  pois: PointOfInterest[];
  group: THREE.Group;
}

/**
 * Build the default Phase 1 POI set: a food bowl and a water bowl,
 * placed in front-left of the shelf so they're visible from the
 * default camera and well within the cat's roam zone.
 */
export function buildPois(): PoiHandle {
  const group = new THREE.Group();
  group.name = "pois";

  const food = makeBowl({ tint: 0x8a3a1d, water: false });
  food.position.set(-7, 0, 4);
  group.add(food);

  const water = makeBowl({ tint: 0x2a4a78, water: true });
  water.position.set(-10, 0, 4);
  group.add(water);

  const pois: PointOfInterest[] = [
    {
      id: "food-bowl",
      kind: "food",
      position: new THREE.Vector3(-7, 0, 4),
      duration: 4,
      clip: "Eat_D",
      mesh: food,
    },
    {
      id: "water-bowl",
      kind: "water",
      position: new THREE.Vector3(-10, 0, 4),
      duration: 4,
      clip: "Drink_D",
      mesh: water,
    },
  ];

  return { pois, group };
}

interface BowlOptions {
  tint: number;
  /** When true, adds a slightly blue disc inside the bowl to suggest
   * water — the bowl alone reads ambiguously. */
  water: boolean;
}

function makeBowl(opts: BowlOptions): THREE.Group {
  const g = new THREE.Group();
  g.name = "bowl";

  // Rim — a torus laid flat. A torus has no flat top face, so it can't
  // z-fight with anything sat below it (unlike a solid cylinder
  // pretending to be a bowl).
  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(0.48, 0.07, 10, 28),
    new THREE.MeshStandardMaterial({
      color: opts.tint,
      roughness: 0.5,
      metalness: 0.05,
    }),
  );
  rim.rotation.x = Math.PI / 2;
  rim.position.y = 0.07;
  rim.castShadow = true;
  rim.receiveShadow = true;
  g.add(rim);

  // Floor — a darker disc nestled inside the rim ring. Sits below the
  // rim's belly so the food/water on top reads as held by the bowl.
  const floor = new THREE.Mesh(
    new THREE.CylinderGeometry(0.46, 0.46, 0.02, 28),
    new THREE.MeshStandardMaterial({
      color: 0x2a1d14,
      roughness: 0.9,
    }),
  );
  floor.position.y = 0.025;
  floor.receiveShadow = true;
  g.add(floor);

  if (opts.water) {
    // Water disc — slightly smaller than the floor and clearly above
    // it, so neither face sits coplanar with anything.
    const liquid = new THREE.Mesh(
      new THREE.CylinderGeometry(0.42, 0.42, 0.018, 28),
      new THREE.MeshStandardMaterial({
        color: 0x6fa8d6,
        roughness: 0.2,
        metalness: 0.1,
        transparent: true,
        opacity: 0.85,
      }),
    );
    liquid.position.y = 0.06;
    g.add(liquid);
  } else {
    // Kibble — a handful of small spheres sat above the floor disc.
    const mat = new THREE.MeshStandardMaterial({
      color: 0x6e4a26,
      roughness: 0.85,
    });
    for (let i = 0; i < 7; i++) {
      const piece = new THREE.Mesh(new THREE.SphereGeometry(0.06, 8, 6), mat);
      const angle = (i / 7) * Math.PI * 2 + Math.random() * 0.4;
      const r = 0.12 + Math.random() * 0.18;
      piece.position.set(
        Math.cos(angle) * r,
        0.07 + Math.random() * 0.02,
        Math.sin(angle) * r,
      );
      piece.castShadow = true;
      g.add(piece);
    }
  }

  return g;
}
